import React, { useEffect } from "react";
import styles from "./Home.module.css";
import { FiMail } from "react-icons/fi";
import { FaPhone } from "react-icons/fa";
import { BiChevronsRight } from "react-icons/bi";

import Contact from "../../components/Contact";
import Separator from "../../components/Separator";
import servicesJson from "../../assets/data/services.json";
import Card from "../../components/Card/Card";
import certifications from "../../assets/data/certifications.json";
import CertificationCard from "../../components/CertificationCard";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useObserveAnimation } from "../../hooks";
import "../../hooks/animations.css";

import bgVideo from "../../assets/videos/bg-video.mp4";

const Home = () => {
  const { t, i18n } = useTranslation();
  const [aboutUsSessionRef, { animateBottom: aboutUsSessionClass }] =
    useObserveAnimation();
  const [certsSessionRef, { animateBottom: certsSessionClass }] =
    useObserveAnimation();

  useEffect(() => {
    document.title = "Home - The Records Hub S.A.";
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.videoSectionContainer}>
        <div className={styles.videoSectionBg} />
        <video className={styles.bgVideo} autoPlay loop playsInline muted>
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className={styles.videoBannerContent}>
          <div className={styles.titleContainer}>
            <h1>
              The Professional Way{" "}
              <Separator
                marginB={"18px"}
                widht={"25px"}
                height={"1.5px"}
                color={"#f7931d"}
              />
            </h1>
          </div>
          <p>
            {t("home.quote")
              .split("\n")
              .map((str, i) => (
                <span key={`quote_${i}`}>
                  {str}
                  <br />
                </span>
              ))}
          </p>
          <div className={styles.buttonsContainer}>
            <Link to={"/about-us"} className={styles.aboutUsButton}>
              {t("home.aboutUsBtn")}
            </Link>
            <Link to={"/services"} className={styles.ourServicesButton}>
              {t("home.ourServicesBtn")}
              <BiChevronsRight size={"30px"} />
            </Link>
          </div>
        </div>
      </div>
      <div className={styles.section_container}>
        <div className={styles.section_content}>
          <div className={styles.section_top_container} ref={aboutUsSessionRef}>
            <h2 className={`${styles.h2} ${aboutUsSessionClass}`}>
              {t("home.aboutUsSession.title")}
            </h2>
            <Separator
              widht={"28px"}
              height={"1.3px"}
              color={"#f7931d"}
              marginT={"30px"}
              marginL={"40px"}
            />
          </div>
          <div className={styles.section_middle_container}>
            <div className={styles.video_and_text_container}>
              <iframe
                className={styles.video}
                src="https://www.youtube-nocookie.com/embed/QMTG8ZlYUMk?si=2mmiO-6C2klvK10R"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>{" "}
              <div className={styles.aboutUsDescription}>
                {t("home.aboutUsSession.content")
                  .split("\n")
                  .map((str, i) => (
                    <p key={`aboutUsSession_content_${i}`} className={styles.p}>
                      {str}
                    </p>
                  ))}
              </div>
            </div>
          </div>
          <div className={styles.section_bottom_container}>
            <div className={styles.contact_us_button}>
              <a
                href="mailto:Info@therecordshub.com"
                className={styles.contact_us_button_content}
                role="button"
              >
                {t("home.aboutUsSession.contactUsBtn")}
              </a>
              <FiMail size={"20px"} className={styles.button_icons} />
            </div>
            <div className={styles.call_us_button}>
              <a
                href="tel:+302113110870"
                className={styles.call_us_button_content}
                role="button"
              >
                {t("home.aboutUsSession.callUsBtn")}
              </a>
              <FaPhone />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.h1WithSeparators} ref={certsSessionRef}>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
        <h1>{t("home.certsSession.title")}</h1>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
      </div>
      <div className={`${styles.certifContainer} ${certsSessionClass}`}>
        {certifications &&
          certifications.map((cert, i) => (
            <CertificationCard {...cert} key={`CertificationCard${i}`} />
          ))}
      </div>
      <div className={styles.h1WithSeparators}>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
        <h1>{t("home.reportsSession.title")}</h1>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
      </div>
      <div
        className={`${styles.certifContainer} ${certsSessionClass}`}
        style={{ justifyContent: "center" }}
      >
        <CertificationCard
          img="/images/ESG_logo.png"
          title={{ en: "TRH ESG Report 2021", el: "Αναφορά TRH ESG 2021" }}
          href="/pdfs/The Records Hub_SR Report 2021_All chapters_final.pdf"
        />
      </div>
      <div className={styles.h1WithSeparators}>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
        <h1>{t("home.servicesSession.title")}</h1>
        <Separator widht={"40px"} height={"1.5px"} color={"#f7931d"} />
      </div>
      <div className={styles.cardsContainer}>
        {servicesJson &&
          servicesJson.map((service, i) => (
            <Card
              key={`Service_Card_${i}`}
              imgPath={service.img}
              title={service.shortTitle[i18n.language]}
              summary={service.summary[i18n.language]}
              to={`/services#${service.id}`}
            />
          ))}
      </div>
      <Contact />
    </div>
  );
};

export default Home;
